import { Box, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { useAuthContext } from "../../providers/Auth/auth.provider";
import { useDashboardActionContext } from "../../pages/dashboard/dashboard-action.provider";
import { AvailableBaseRoutes } from "../../utils/constants";
import { getReportingPeriodLabel } from "../../utils/reporting-period-label.util";
import { PrimaryColors } from "../../style/Colors";
import { Loading } from "../../components/loading/loading.component";
import { PomReportingHeader } from "../../components/reporting-header/pom-reporting-header.component";
import { PomReportingTable } from "../../components/reporting-table/pom-reporting-table.component";
import { PomContractSelectionDialog } from "../../components/reporting-dialog/pom-contract-selection-dialog.component";
import { PomReportingExcelImportDialog } from "../../components/reporting-dialog/pom-reporting-excel-import.dialog";
import { PomReportingExcelExportDialog } from "../../components/reporting-dialog/pom-reporting-excel-export.dialog";
import { QuestionDialog } from "../../components/Dialogs/question-dialog";
import { PomReportingProvider, usePomReportingContext } from "./pom-reporting.provider";

const useStyles = makeStyles()({
  pageContainer: {
    padding: "16px",
  },
  noContractText: {
    textAlign: "center",
    padding: "40px",
  },
  titleRow: {
    color: PrimaryColors.base,
    fontWeight: "bold",
    marginBottom: "10px",
  },
  contractNumberClickable: {
    cursor: "pointer",
    textDecoration: "underline",
    textDecorationStyle: "dotted",
    "&:hover": {
      textDecoration: "underline",
      textDecorationStyle: "solid",
    },
  },
});

const PomReportingContent: FC = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const { internalUser } = useAuthContext();
  const { contractId, setContractId } = useDashboardActionContext();
  const {
    currentContractNo,
    currentTakeBackSystemName,
    currentReportingNo,
    currentStatus,
    reportingList,
    positions,
    isLoading,
    isPositionsError,
    isDirty,
    hasDraft,
    isSavingDraft,
    draftReportingNos,
    hasUnsavedDraftChanges,
    hasUnsavedDataForNavigation,
    isImportDialogOpen,
    isExportDialogOpen,
    expandedPositionNos,
    handleReportingSelected,
    onQuantityChange,
    handleToggleHistory,
    submitReporting,
    resetChanges,
    saveDraft,
    discardDraft,
    openImportDialog,
    closeImportDialog,
    openExportDialog,
    closeExportDialog,
    applyImportedPositions,
  } = usePomReportingContext();

  const [isContractSelectionOpen, setIsContractSelectionOpen] = useState<boolean>(false);
  const [pendingContractNo, setPendingContractNo] = useState<string | undefined>(undefined);
  const [pendingReportingNo, setPendingReportingNo] = useState<number | undefined>(undefined);

  const pomContracts = useMemo(
    () => internalUser?.group?.pomContracts?.filter((c) => c.active) ?? [],
    [internalUser],
  );
  const hasMultipleContracts = pomContracts.length > 1;

  // Auto-open the selection dialog when no contract is selected yet
  useEffect(() => {
    if (hasMultipleContracts && contractId === undefined) {
      setIsContractSelectionOpen(true);
    }
  }, [hasMultipleContracts, contractId]);

  const currentReporting = reportingList.find((r) => r.reportingNo === currentReportingNo);

  const handleContractChosen = (contractNumber: string): void => {
    setIsContractSelectionOpen(false);
    if (contractNumber === contractId) return;
    if (isDirty) {
      setPendingContractNo(contractNumber);
    } else {
      setContractId(contractNumber);
    }
  };

  const handleContractSwitchConfirm = async (): Promise<void> => {
    if (pendingContractNo === undefined) return;
    resetChanges();
    setContractId(pendingContractNo);
    setPendingContractNo(undefined);
  };

  const handleContractSwitchCancel = (): void => {
    setPendingContractNo(undefined);
  };

  const handlePeriodSelected = (reportingNo: number): void => {
    if (reportingNo === currentReportingNo) {
      return;
    }

    if (hasUnsavedDataForNavigation) {
      setPendingReportingNo(reportingNo);
      return;
    }

    handleReportingSelected(reportingNo);
  };

  const handlePeriodSwitchConfirm = async (): Promise<void> => {
    if (pendingReportingNo === undefined) {
      return;
    }

    handleReportingSelected(pendingReportingNo);
    setPendingReportingNo(undefined);
  };

  const handlePeriodSwitchCancel = (): void => {
    setPendingReportingNo(undefined);
  };

  const handleContractSelectionCancelled = (): void => {
    setIsContractSelectionOpen(false);
    if (contractId === undefined) {
      history.push(AvailableBaseRoutes.Portal);
    }
  };

  const prefixText = currentReporting
    ? [
        getReportingPeriodLabel(currentReporting.reportingPeriodStartDate, currentReporting.reportingPeriodEndDate),
        currentTakeBackSystemName,
      ]
        .filter(Boolean)
        .join(" \u2013 ")
    : undefined;

  return (
    <>
      <PomContractSelectionDialog
        open={isContractSelectionOpen}
        contracts={pomContracts}
        onContractSelected={handleContractChosen}
        onCancel={handleContractSelectionCancelled}
      />
      <QuestionDialog
        isOpen={pendingContractNo !== undefined}
        title={t("general.warning")}
        message={t("pom.reportings.contract_switch.unsaved_warning")}
        acceptText={t("general.leave")}
        onAccept={handleContractSwitchConfirm}
        onClose={handleContractSwitchCancel}
        displayStyle="warning"
      />
      <QuestionDialog
        isOpen={pendingReportingNo !== undefined}
        title={t("general.warning")}
        message={t("pom.reportings.period_switch.unsaved_warning")}
        acceptText={t("general.leave")}
        onAccept={handlePeriodSwitchConfirm}
        onClose={handlePeriodSwitchCancel}
        displayStyle="warning"
      />
      {!currentContractNo ? (
        <Typography className={classes.noContractText}>{t("pom.reportings.no_contract")}</Typography>
      ) : (
        <Loading isLoading={isLoading}>
          <Box className={classes.pageContainer}>
            <Typography variant="h5" className={classes.titleRow}>
              {prefixText && <>{prefixText} &ndash; </>}
              <span
                className={hasMultipleContracts ? classes.contractNumberClickable : undefined}
                onClick={hasMultipleContracts ? () => setIsContractSelectionOpen(true) : undefined}
              >
                {currentContractNo}
              </span>
            </Typography>
            <PomReportingHeader
              reportingList={reportingList}
              currentReportingNo={currentReportingNo}
              positions={positions}
              isDirty={isDirty}
              isLoading={isLoading}
              hasDraft={hasDraft}
              isSavingDraft={isSavingDraft}
              draftReportingNos={draftReportingNos}
              hasUnsavedDraftChanges={hasUnsavedDraftChanges}
              onSubmit={submitReporting}
              onReportingSelected={handlePeriodSelected}
              onSaveDraft={saveDraft}
              onDiscardDraft={discardDraft}
              onOpenImportDialog={openImportDialog}
              onOpenExportDialog={openExportDialog}
            />
            {currentReportingNo !== undefined && (
              <PomReportingTable
                positions={positions}
                disabled={false}
                onQuantityChange={onQuantityChange}
                expandedPositionNos={expandedPositionNos}
                onToggleHistory={handleToggleHistory}
                contractNo={currentContractNo}
                reportingNo={currentReportingNo}
                isError={isPositionsError}
              />
            )}
            {currentContractNo && currentReportingNo !== undefined && (
              <PomReportingExcelImportDialog
                open={isImportDialogOpen}
                contractNo={currentContractNo}
                reportingNo={currentReportingNo}
                currentStatus={currentStatus}
                onApply={applyImportedPositions}
                onClose={closeImportDialog}
              />
            )}
            {currentContractNo && (
              <PomReportingExcelExportDialog
                open={isExportDialogOpen}
                contractNo={currentContractNo}
                reportingList={reportingList}
                onClose={closeExportDialog}
              />
            )}
          </Box>
        </Loading>
      )}
    </>
  );
};

export const PomReportingPage: FC = () => {
  const { internalUser } = useAuthContext();
  const history = useHistory();

  if (!internalUser?.group) {
    history.push(AvailableBaseRoutes.Portal);
    return null;
  }

  return (
    <PomReportingProvider>
      <PomReportingContent />
    </PomReportingProvider>
  );
};
